import { Validators } from './validators';
import { FeatureFlag } from 'constants-flags';

import { ValidatorRegistry } from '../ValidatorRegistry';
import { SupportedSchema } from '../baseTypes/TypeBase';
import { PropertiesInterface } from './Property';
import { PropertySecurity, SecurityModesEnum, SecurityRuleTypeEnum } from './PropertySecurity';
import { Types } from './Types';
import { DateTimeRangeType } from './baseTypes/DateTimeRange';
import { UrlType } from './baseTypes/Url';
import './baseTypes';

type SecurityContext = {
  mode: SecurityModesEnum;
  type: SecurityRuleTypeEnum;
  validationFlags?: Record<string, boolean>;
};

function shouldSkipRequiredValidator(
  security: SecurityContext | undefined,
  defSecure: PropertySecurity | undefined
): boolean {
  if (security?.mode === SecurityModesEnum.Update) {
    return true;
  }

  if (
    security &&
    defSecure?.create &&
    security.mode === SecurityModesEnum.Create &&
    security.type === SecurityRuleTypeEnum.Creator &&
    !defSecure.create.some(s => s.type === SecurityRuleTypeEnum.Creator)
  ) {
    return true;
  }

  return false;
}

/**
 * Creates a validation object using yup from a property type definition.
 *
 * depending on the security object passed in, it will include or exclude
 * different validation based on the security settings of each property.
 */
export function createShapeFromProperties(
  obj?: PropertiesInterface,
  security?: {
    mode: SecurityModesEnum;
    type: SecurityRuleTypeEnum;
    validationFlags?: Record<string, boolean>;
  }
) {
  if (!obj) {
    return {};
  }

  const shape: Record<string, SupportedSchema> = {};

  Object.entries(obj).forEach(([key, def]) => {
    const type = Types.create(def);

    // scoped to DateTimeRangeType only, since it's the only type whose
    // buildSchema() branches on this flag. Must be set before anything below
    // accesses `type.schema`.
    if (type instanceof DateTimeRangeType) {
      type.isFixDateTimeRangeValidationEnabled = Boolean(
        security?.validationFlags?.[FeatureFlag.FixDateTimeRangeValidation]
      );
    }

    if (type instanceof UrlType) {
      type.restrictToHttpProtocols = Boolean(
        security?.validationFlags?.[FeatureFlag.RestrictWebsiteUrlSchemes]
      );
    }

    if (def.validators) {
      def.validators.forEach(validator => {
        const Validator = ValidatorRegistry.create(type, validator);

        if (Validator?.arrayOnly) {
          return;
        }

        if (
          validator.name === Validators.Required.name &&
          shouldSkipRequiredValidator(security, def.secure)
        ) {
          return;
        }

        Validator?.attach(type);
        shape[key] = type.schema;
      });
    }

    if (def.isArray) {
      if (def.validators) {
        def.validators.forEach(validator => {
          const Validator = ValidatorRegistry.create(type, validator);

          if (!Validator) {
            throw new Error(
              `Invalid validator ${validator.name} attached to ${key}`
            );
          }

          if (Validator.array) {
            Validator.attach(type);
            shape[key] = type.arraySchema;
          }
        });
      }
    }

    // Some types (e.g. DateTimeRange) encode structural constraints in their
    // own schema that must run even when the property has no explicit
    // validators. Such types opt in via `alwaysValidate` and are always
    // included in the shape. For DateTimeRangeType this is additionally
    // gated behind its own rollout flag, set above.
    if (
      shape[key] === undefined &&
      type.alwaysValidate &&
      (!(type instanceof DateTimeRangeType) ||
        type.isFixDateTimeRangeValidationEnabled)
    ) {
      shape[key] = type.schema;
    }
  });

  return shape;
}
